import type { Transaction, TransactionAvailableAction, TransactionGroup } from "../shared/types";
import { readTransactionGroup, transactionPath } from "./transaction-groups";

export type TransactionActionCode = "PAY" | "SUBMIT_SUPPLEMENT" | "SELECT_TRANSFER" | "CANCEL_TRANSACTION" | "CANCEL_CARD_PAYMENT" | "VIEW_PAYMENT_STATUS" | "VIEW_TRANSFER_STATUS" | "CONTACT_SUPPORT";
export const customerSupportPhone = "1522-5663";
export const customerSupportTel = "tel:15225663";
const knownStatuses = new Set(["REGISTERED", "UNDER_REVIEW", "REVIEWING", "SUPPLEMENT_REQUIRED", "PAYMENT_PENDING", "PAYMENT_FAILED", "PAYMENT_PROCESSING", "PAYMENT_UNKNOWN", "PAYMENT_CANCELING", "TRANSFER_SELECTION_PENDING", "TRANSFER_READY", "TRANSFER_PROCESSING", "TRANSFER_UNKNOWN", "TRANSFER_FAILED", "COMPLETED", "CANCELED", "REJECTED", "PAYMENT_EXPIRED"]);
const labels: Record<TransactionActionCode, string> = {
  PAY: "카드로 결제하기", SUBMIT_SUPPLEMENT: "서류 보완하기", SELECT_TRANSFER: "송금하기",
  CANCEL_TRANSACTION: "거래 취소하기", CANCEL_CARD_PAYMENT: "결제 취소하기", VIEW_PAYMENT_STATUS: "결제 상태 보기", VIEW_TRANSFER_STATUS: "송금 상태 보기", CONTACT_SUPPORT: "고객센터 문의",
};
const ranks: Record<TransactionActionCode, number> = { SUBMIT_SUPPLEMENT: 0, PAY: 1, SELECT_TRANSFER: 2, VIEW_PAYMENT_STATUS: 3, VIEW_TRANSFER_STATUS: 4, CONTACT_SUPPORT: 5, CANCEL_TRANSACTION: 6, CANCEL_CARD_PAYMENT: 7 };
const homeCodes = new Set<TransactionActionCode>(["PAY", "SUBMIT_SUPPLEMENT", "SELECT_TRANSFER"]);
export interface PresentedTransactionAction extends TransactionAvailableAction {
  readonly code: TransactionActionCode;
  readonly label: string;
  readonly route?: string;
  readonly primary: boolean;
}

/** Server entries are the only positive authority. Unknown states fail closed. */
export function presentedTransactionActions(transaction: Transaction, group: TransactionGroup = readTransactionGroup()): readonly PresentedTransactionAction[] {
  if (!knownStatuses.has(transaction.status) || !Array.isArray(transaction.availableActions)) return [];
  const available: readonly TransactionAvailableAction[] = transaction.availableActions;
  const unique = new Set<string>();
  const actions = available.filter(action => {
    if (!action || !Object.prototype.hasOwnProperty.call(labels, action.code) || typeof action.allowed !== "boolean" || unique.has(action.code)) return false;
    unique.add(action.code);
    return true;
  }).map(action => {
    const code = action.code as TransactionActionCode;
    const path = code === "PAY" || code === "VIEW_PAYMENT_STATUS" ? `/app/transactions/${transaction.id}/payment`
      : code === "SELECT_TRANSFER" || code === "VIEW_TRANSFER_STATUS" ? `/app/transactions/${transaction.id}/transfer`
        : code === "SUBMIT_SUPPLEMENT" ? `/app/transactions/new?supplementFor=${transaction.id}`
          : code === "CONTACT_SUPPORT" ? "/app/inquiries" : undefined;
    return { ...action, code, label: labels[code], route: path && (code === "CONTACT_SUPPORT" ? path : transactionPath(path, group)), primary: false };
  }).sort((a, b) => ranks[a.code] - ranks[b.code]);
  const primary = actions.find(action => action.allowed && !(["CANCEL_TRANSACTION", "CANCEL_CARD_PAYMENT"].includes(action.code)));
  return actions.map(action => ({ ...action, primary: action === primary })).sort((a, b) => Number(b.primary) - Number(a.primary));
}

export function allowedTransactionAction(transaction: Transaction, code: TransactionActionCode, group?: TransactionGroup): PresentedTransactionAction | undefined {
  return presentedTransactionActions(transaction, group).find(action => action.code === code && action.allowed === true);
}
export function homeTransactionAction(transaction: Transaction): PresentedTransactionAction | undefined {
  return presentedTransactionActions(transaction, "ALL").find(action => action.allowed && homeCodes.has(action.code));
}
export function transactionActionBlockedMessage(transaction: Transaction, code: TransactionActionCode): string {
  return presentedTransactionActions(transaction).find(action => action.code === code)?.blockedReasonMessage?.trim()
    || "현재 이 행동을 진행할 수 없습니다. 거래 상세에서 최신 상태를 확인해 주세요.";
}
